type ErrorResponseData = {
  title?: unknown;
  detail?: unknown;
  message?: unknown;
  errors?: unknown;
};

const MAX_VALIDATION_MESSAGES = 5;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const addUniqueMessage = (messages: string[], value: unknown) => {
  const message = asNonEmptyString(value);
  if (message && !messages.includes(message)) {
    messages.push(message);
  }
};

const collectValidationMessages = (
  validationErrors: unknown,
  options: { includeStringValues?: boolean } = { includeStringValues: true }
): string[] => {
  const messages: string[] = [];

  if (Array.isArray(validationErrors)) {
    validationErrors.forEach((entry) => addUniqueMessage(messages, entry));
    return messages;
  }

  if (!isRecord(validationErrors)) {
    return messages;
  }

  Object.values(validationErrors).forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => addUniqueMessage(messages, entry));
      return;
    }

    if (options.includeStringValues) {
      addUniqueMessage(messages, value);
    }
  });

  return messages;
};

const formatValidationMessages = (messages: string[]): string | undefined => {
  if (messages.length === 0) {
    return undefined;
  }

  const visibleMessages = messages.slice(0, MAX_VALIDATION_MESSAGES);
  const hiddenCount = messages.length - visibleMessages.length;
  const suffix = hiddenCount > 0 ? ` (+${hiddenCount} more)` : '';

  return `${visibleMessages.join('; ')}${suffix}`;
};

export const getApiErrorMessageFromData = (data: unknown, fallback: string): string => {
  const plainText = asNonEmptyString(data);
  if (plainText) {
    return plainText;
  }

  if (!isRecord(data)) {
    return fallback;
  }

  const responseData = data as ErrorResponseData;
  const validationMessage =
    formatValidationMessages(collectValidationMessages(responseData.errors)) ??
    formatValidationMessages(collectValidationMessages(data, { includeStringValues: false }));

  if (validationMessage) {
    return validationMessage;
  }

  return (
    asNonEmptyString(responseData.detail) ??
    asNonEmptyString(responseData.message) ??
    asNonEmptyString(responseData.title) ??
    fallback
  );
};

export const formatApiErrorResponse = (status: number | undefined, data: unknown, reasonPhrase?: string): string => {
  const fallback = status ? `${reasonPhrase ?? 'Request failed'} (${status})` : reasonPhrase ?? 'Request failed';
  const message = getApiErrorMessageFromData(data, fallback);

  return message === fallback ? fallback : `${fallback}: ${message}`;
};
